<script>
  import { createEventDispatcher } from "svelte";
  import upperCase from "lodash/upperCase";
  import { navigate } from "svelte-routing";

  import { clickOutside } from "svelte-utilities";
  import ReviewerMoreButton from "#lib/home/ReviewerMoreButton.svelte";
  import DownloadIcon from "@local/assets/icons/download.svg";
  import ShareIcon from "@local/assets/icons/share.svg";
  import ProfileIcon from "@local/assets/icons/profile.svg";
  import ContenteditableInput from "#lib/sidebar/ContenteditableInput.svelte";
  import DocumentThumbnail from "#lib/DocumentThumbnailMini.svelte";
  import StatusButton from "#lib/StatusButton.svelte";
  import SpinnerIcon from "@local/assets/icons/spinner-lg.svg";

  import formatHumanTime from "@local/extensions/formatters/format-human-time.js";
  import getUserInitials from "@local/extensions/formatters/get-user-initials.js";
  import { profile } from "#src/stores/auth.js";
  import { UX_MODE } from "#src/env.js";

  export let job;
  export let deleting = false;
  export let assignToOptions = [];

  let expanded = false;
  let showAssignTo = false;

  $: groupBy = job?.group_data.settings.output_group_by || "type";
  $: showTotalUnits = job?.group_data.settings.output_show_total_units ?? true;
  $: showTotalCategories = job?.group_data.settings.output_show_total_categories ?? true;
  $: capabilities = $profile?.profile_capabilities?.capabilities || {};
  $: editDisabled = job?.status.startsWith("tally:") && !capabilities.edit_tally_jobs;
  $: showProps = !job?.status.startsWith("tally:") || job?.status === "tally:complete";
  $: showMoreButton =
    !["tally:draft", "tally:pending", "tally:blocked"].includes(job?.status) || capabilities.edit_tally_jobs;
  $: navigatable =
    !job?.status.startsWith("tally:") ||
    job?.status === "tally:unsubmitted" ||
    job?.status === "tally:complete" ||
    capabilities.edit_tally_jobs === true;
  $: deletable =
    capabilities.delete_jobs === true &&
    (!job?.status.startsWith("tally:") || capabilities.edit_tally_jobs === true);

  const dispatch = createEventDispatcher();

  function itemCount(count, label = "unit") {
    if (!count) return `0 ${label}s`;
    if (count === 0) return `0 ${label}s`;
    if (count === 1) return `1 ${label}`;
    return `${count} ${label}s`;
  }

  function nav(doc) {
    if (doc.type === "remote") {
      window.open(doc.data.url, "_blank");
    } else {
      navigate(`/jobs/${job.id}/documents/${doc.id}`);
    }
  }

  function assignTo(option) {
    showAssignTo = false;
    dispatch("update-job", {
      id: job.id,
      update: { assigned_to_id: option.value },
    });
  }

  function navJob(id) {
    if (!navigatable) {
      return;
    }

    if (UX_MODE === "shpx") {
      navigate(`/jobs/${id}/documents`);
    } else if (job.status === "tally:unsubmitted") {
      navigate(`/tally-request/${id}`);
    } else if (job.status === "draft" || job.status === "tally:draft") {
      navigate(`/jobs/${id}/documents`);
    } else {
      navigate(`/jobs/${id}/summary`);
    }
  }
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  class="w-full text-left relative border py-4 border-gray-300 rounded-lg shadow reviewer-card"
  on:dblclick={() => navJob(job.id)}
  class:navigatable
  class:unsubmitted={job.status === "tally:unsubmitted"}
  class:opacity-50={deleting}
  class:border-dashed={deleting}>
  <div class:invisible={deleting} class="flex text-xs">
    <div class="basis-1/2 flex-none flex px-4">
      <div class="grow flex flex-col justify-between gap-2">
        <div>
          <div class="flex justify-between">
            <div class="font-bold" class:italic={!navigatable}>
              {job.name}
            </div>
            <div class="flex gap-1 items-center relative">
              <div class="text-gray-500 italic">
                {formatHumanTime(job.created_at)}
              </div>
              {#if job.assigned_to_username}
                <button on:click={() => (showAssignTo = !showAssignTo)}>
                  <div
                    class="profile-circle relative"
                    style="background-color: {job.assigned_to_user_color ||
                      '#000'}; border-color: {job.assigned_to_user_color || '#000'}">
                    {upperCase(
                      getUserInitials({
                        username: job.assigned_to_username,
                        email: job.assigned_to_email,
                      }),
                    )}
                  </div>
                </button>
              {:else if !job.assigned_to}
                <button on:click={() => (showAssignTo = !showAssignTo)}>
                  <div class="text-gray-400">
                    <ProfileIcon />
                  </div>
                </button>
              {/if}
              {#if showAssignTo}
                <div
                  class="right-0 top-0 mt-5 dropdown-container"
                  use:clickOutside
                  on:outclick={() => (showAssignTo = false)}>
                  {#each assignToOptions as option}
                    <button
                      class="flex gap-2 items-center p-2 dropdown-button-item"
                      on:click={() => assignTo(option)}>
                      {#if option.initials}
                        <div
                          class="profile-circle relative"
                          style="background-color: {option.color || '#000'}; border-color: {option.color ||
                            '#000'}">
                          {option.initials}
                        </div>
                      {:else}
                        <div class="flex-none h-4 w-4" />
                      {/if}
                      <div class:text-gray-400={!option.value} class="truncate">
                        {option.label}
                      </div>
                    </button>
                  {/each}
                </div>
              {/if}
            </div>
          </div>
          {#if job.customer_record}
            <div>
              {job.customer_record.name} ({job.customer_record.email})
            </div>
          {:else if job.customer_text}
            <div>
              {job.customer_text}
            </div>
          {/if}
          {#if showProps}
            {#if groupBy === "type"}
              <div>
                {itemCount(job.items_quantity || 0)}
              </div>
              {#if showTotalUnits}
                <div>
                  {itemCount(job.types_count || 0, "type")}
                </div>
              {/if}
            {:else if groupBy === "opening"}
              {#if showTotalCategories}
                <div>
                  {itemCount(job.collections_quantity || 0, "opening")}
                </div>
              {/if}
              {#if showTotalUnits}
                <div>
                  {itemCount(job.items_quantity || 0)}
                </div>
              {/if}
            {/if}
          {:else if job?.status === "tally:blocked" || job?.status === "tally:pending"}
            {#if capabilities.edit_tally_jobs}
              <div class="text-red-500 py-2 w-full">
                <ContenteditableInput
                  label={null}
                  value={job?.status_text}
                  placeholder="Job Status"
                  border
                  on:input={(e) =>
                    dispatch("update-job", { id: job.id, update: { status_text: e.detail.value } })} />
              </div>
            {:else if job?.status_text}
              <div class="text-red-500 py-2">
                {job.status_text}
              </div>
            {/if}
          {/if}
        </div>
        <div class="flex gap-2 items-center">
          {#if showProps}
            <button class="btn btn-compact flex gap-1" on:click={() => navJob(job.id)}>
              <ShareIcon />
              <div>Review</div>
            </button>
            <button
              class="btn btn-compact flex gap-1"
              on:click={() => dispatch("download-job", { id: job.id })}>
              <DownloadIcon />
              <div>Download</div>
            </button>
          {/if}
          {#if showMoreButton}
            <ReviewerMoreButton
              {deletable}
              jobid={job.id}
              on:archive-job={() => dispatch("archive-job", { id: job.id })}
              on:clone-job={() => dispatch("clone-job", { id: job.id })}
              on:delete-job={() => dispatch("delete-job", { id: job.id })}
              on:export-json={() => dispatch("export-json", { id: job.id })} />
          {/if}
        </div>
      </div>
    </div>
    {#if showProps}
      {@const allDocs =
        UX_MODE === "shpx"
          ? job.documents_array?.filter((doc) => doc.type === "virtual") || []
          : (job.documents_array ?? [])}
      {@const visibleDocs = expanded ? allDocs : allDocs.slice(0, 3)}
      <div class="basis-1/2 px-4 border-l border-gray-200">
        <div class="flex items-center gap-4 mb-2">
          <div class="font-bold">Documents</div>
          {#if job.documents_count > 3}
            <button class="text-xs btn-text" on:click={() => (expanded = !expanded)}>
              {expanded ? "Show less" : "Show all"}
            </button>
          {/if}
        </div>
        {#if allDocs.length}
          <div class="flex gap-4 flex-wrap">
            {#each visibleDocs as doc}
              {#key doc.id}
                <button
                  class="w-28 border rounded-lg bg-white p-2 shadow text-left hover:shadow-lg"
                  on:click={() => nav(doc)}>
                  <div class="relative w-full aspect-square border">
                    <DocumentThumbnail {doc} />
                  </div>
                  <div class="truncate text-xs mt-1">
                    {doc.name}
                  </div>
                </button>
              {/key}
            {/each}
          </div>
        {:else}
          <div class="text-gray-500 italic">No documents</div>
        {/if}
      </div>
    {/if}
    <div class="absolute top-2 right-2 flex gap-2 items-center">
      <StatusButton
        value={job.status}
        disabled={editDisabled}
        on:change={(e) => dispatch("update-job", { id: job.id, update: { status: e.detail.value } })} />
    </div>
  </div>
  {#if deleting}
    <div class="absolute top-0 left-0 flex gap-4 items-center p-4">
      <div class="text-gray-800 h-8 w-8 relative">
        <div class="absolute">
          <div class="animate-spin">
            <SpinnerIcon />
          </div>
        </div>
      </div>
      <div class="text-gray-800 italic text-sm">Deleting job...</div>
    </div>
  {/if}
</div>

<style lang="scss">
  .reviewer-card {
    @apply bg-gray-50;

    &.navigatable {
      @apply cursor-pointer;

      &:not(.unsubmitted) {
        @apply bg-white;
      }

      &:hover {
        @apply shadow-lg;
      }
    }
  }
</style>
